import escapeRegex from 'quotemeta'

import {
  Filters,
  Project,
  EngageApp,
  ReachProject,
  Direction,
  AllChannelTypeKeys,
  MobilePlatforms,
  Stats,
} from '../types'

export function runFilter(
  filter: Filters,
  engageApps: EngageApp[],
  reachProjects: ReachProject[],
  stats: Stats
): Project[] {
  const projects: Project[] = []
  if (filter.type === 'reach' || filter.type === 'all') {
    projects.push(...reachProjects)
  }
  if (filter.type !== 'reach') {
    projects.push(...engageApps)
  }
  const list: Project[] = projects.filter(
    (card: Project) =>
      filterContains(filter.contains, card) &&
      filterStatus(filter.status, card) &&
      filterOwnership(filter.ownership, card) &&
      filterForProjectType(filter, card, stats)
  )
  return list.sort(sortBy(filter.on, filter.direction))
}

function filterContains(contains: Filters['contains'], card: Project): boolean {
  if (!contains) {
    return true
  }

  const regEx = RegExp(escapeRegex(contains), 'i')
  return regEx.test(card.name)
}

function filterForProjectType(
  filter: Filters,
  card: Project,
  stats: Stats
): boolean {
  if (!stats.hasEngage) {
    // If engage is not enabled bypass the rest and just apply the reach filters.
    return isEngageApp(card) ? false : filterReachOptions(filter, card)
  }

  if (filter.type === 'all') {
    return true
  }

  if (filter.type !== 'reach' && filter.type !== 'mobile') {
    // If it's not reach or mobile here, it's actually a 'platform' that's
    // listed in the channel type dropdown. Filter drop any reach projects and
    // filter what's left by platform.

    if (!isEngageApp(card)) {
      return false
    }

    return filterPlatform(filter.type, card)
  }

  if (isEngageApp(card)) {
    // These are just mobile apps, first filter them by to only ones that
    // include a mobile platform type.
    return filterMobile(card) && filterPlatform(filter.selectedPlatform, card)
  } else {
    return filterReachOptions(filter, card)
  }
}

function filterReachOptions(filter: Filters, card: ReachProject): boolean {
  return (
    filterCompany(filter.companyName, card) &&
    filterPassType(filter.passType, card)
  )
}

function filterOwnership(
  ownership: Filters['ownership'],
  card: Project
): boolean {
  const owned = isEngageApp(card) ? !card.shared : card._meta.isOwner
  return (
    !ownership ||
    (ownership === 'owner' && owned) ||
    (ownership === 'team' && !owned)
  )
}

function filterPassType(
  type: Filters['passType'],
  card: ReachProject
): boolean {
  return !type || type === 'all' || card._meta.friendlyType === type
}

function filterCompany(
  name: Filters['companyName'],
  card: ReachProject
): boolean {
  return !name || name === 'all' || card._meta.companyName === name
}

function filterMobile(card: EngageApp): boolean {
  const mobileKeys = Object.keys(MobilePlatforms)
  return card.platforms.some((plat) => mobileKeys.includes(plat))
}

function filterPlatform(
  platform: 'all' | AllChannelTypeKeys | undefined,
  card: EngageApp
): boolean {
  return !platform || platform === 'all' || card.platforms.includes(platform)
}

function filterStatus(status: Filters['status'], card: Project): boolean {
  if (!status) {
    return true
  }

  if (!isEngageApp(card)) {
    return false
  }

  return (
    (status === 'live' && card.type === 'production') ||
    (status === 'test' && card.type !== 'production')
  )
}

export function isEngageApp(card: Project): card is EngageApp {
  return card.hasOwnProperty('key') && card.hasOwnProperty('created')
}

type SortValue = 1 | -1 | 0
type SortFunc = (a: Project, b: Project) => SortValue
function sortBy(key: Filters['on'], direction: Direction): SortFunc {
  const func: SortFunc = function sort(a, b) {
    const valA = getKey(a, key).toUpperCase()
    const valB = getKey(b, key).toUpperCase()
    if (valA < valB) {
      return direction === Direction.Desc ? 1 : -1
    }
    if (valA > valB) {
      return direction === Direction.Desc ? -1 : 1
    }
    return 0
  }

  return func

  function getKey(obj: Project, k: 'created' | 'name'): string {
    // Special case created/createdAt because the two types store this value in different keys
    if (k === 'created') {
      if (isEngageApp(obj)) {
        return obj.created
      }
      return obj.createdAt
    }

    // if not 'created', 'name' is the only other valid key here.
    return obj.name
  }
}
